/* eslint-disable @typescript-eslint/no-explicit-any */
import { Modal, Button, Form, ListGroup } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import * as Icon from 'react-bootstrap-icons';

import {
  GetEmailConfig,
  SetEmailConfig,
} from '../../services/ApiConsent';

interface EmailConfigModalProps {
  showModal: boolean;
  hideModal: () => void;
}

interface EmailConfigType {
  emailRecipients: string[];
  isEnabled: boolean;
  smtpHost?: string;
  smtpPort?: number;
  enableSsl?: boolean;
  fromEmail?: string;
  fromName?: string;
  updatedBy?: string;
  updatedDate?: string;
}

export const EmailConfigModal = ({ showModal, hideModal }: EmailConfigModalProps) => {
  const [emailConfig, setEmailConfig] = useState<EmailConfigType>({
    emailRecipients: [],
    isEnabled: true,
    smtpHost: '',
    smtpPort: 25,
    enableSsl: false,
    fromEmail: '',
    fromName: '',
  });
  const [newEmail, setNewEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (showModal) {
      loadEmailConfig();
    }
  }, [showModal]);

  const loadEmailConfig = async () => {
    try {
      setIsLoading(true);
      const config = await GetEmailConfig();
      console.log('[EmailConfig] GET response:', config);
      if (config) {
        setEmailConfig(config);
      }
    } catch (error) {
      console.error('Error loading email config:', error);
      toast.error('Failed to load email configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim();
    
    if (!trimmedEmail) {
      toast.warning('Please enter an email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (emailConfig.emailRecipients.includes(trimmedEmail)) {
      toast.warning('This email is already in the list');
      return;
    }

    setEmailConfig({
      ...emailConfig,
      emailRecipients: [...emailConfig.emailRecipients, trimmedEmail],
    });
    setNewEmail('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmailConfig({
      ...emailConfig,
      emailRecipients: emailConfig.emailRecipients.filter(
        (email) => email !== emailToRemove
      ),
    });
  };

  const handleSave = async () => {
    try {
      if (emailConfig.isEnabled && emailConfig.emailRecipients.length === 0) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'No Email Recipients',
          text: 'Email notifications are enabled but no recipients are configured. Continue?',
          showCancelButton: true,
          confirmButtonText: 'Yes, Save',
          cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;
      }

      setIsLoading(true);
      console.log('[EmailConfig] POST body:', emailConfig);
      await SetEmailConfig(emailConfig);

      toast.success('Email configuration saved successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });

      hideModal();
    } catch (error) {
      console.error('Error saving email config:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to save email configuration. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  return (
    <Modal 
      show={showModal} 
      onHide={hideModal} 
      size="lg" 
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Email Notification Configuration</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Enable/Disable Toggle */}
        <Form.Group className="mb-4">
          <Form.Label style={{ fontWeight: 'bold' }}>Email Notifications</Form.Label>
          <div>
            <Form.Check
              type="switch"
              id="email-notifications-switch"
              label={emailConfig.isEnabled ? 'Enabled' : 'Disabled'}
              checked={emailConfig.isEnabled}
              onChange={(e) =>
                setEmailConfig({ ...emailConfig, isEnabled: e.target.checked })
              }
            />
          </div>
          <Form.Text className="text-muted">
            {emailConfig.isEnabled
              ? 'HR will receive email notifications when consent purposes are changed'
              : 'Email notifications are disabled'}
          </Form.Text>
        </Form.Group>

        {/* Add Email Input */}
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 'bold' }}>Add Email Recipients</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              type="email"
              placeholder="Enter email address (e.g., hr@pandora.net)"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!emailConfig.isEnabled || isLoading}
            />
            <Button
              variant="primary"
              onClick={handleAddEmail}
              disabled={!emailConfig.isEnabled || isLoading}
            >
              <Icon.PlusCircle className="me-1" />
              Add
            </Button>
          </div>
        </Form.Group>

        {/* Email List */}
        <Form.Group>
          <Form.Label style={{ fontWeight: 'bold' }}>
            Email Recipients ({emailConfig.emailRecipients.length})
          </Form.Label>
          {emailConfig.emailRecipients.length === 0 ? (
            <div
              className="text-center p-4 text-muted"
              style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}
            >
              <Icon.Envelope size={32} className="mb-2 opacity-50" />
              <p className="mb-0">No email recipients configured</p>
            </div>
          ) : (
            <ListGroup>
              {emailConfig.emailRecipients.map((email, index) => (
                <ListGroup.Item
                  key={index}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div className="d-flex align-items-center">
                    <Icon.Envelope className="me-2 text-primary" />
                    <span>{email}</span>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveEmail(email)}
                    disabled={!emailConfig.isEnabled || isLoading}
                  >
                    <Icon.Trash />
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Form.Group>

        {/* SMTP Configuration */}
        <hr />
        <Form.Label style={{ fontWeight: 'bold' }}>SMTP Configuration</Form.Label>
        <div className="row mb-3">
          <div className="col-8">
            <Form.Group>
              <Form.Label>SMTP Host</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. smtprelay.global.local"
                value={emailConfig.smtpHost || ''}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtpHost: e.target.value })}
                disabled={isLoading}
              />
            </Form.Group>
          </div>
          <div className="col-4">
            <Form.Group>
              <Form.Label>SMTP Port</Form.Label>
              <Form.Control
                type="number"
                placeholder="25"
                value={emailConfig.smtpPort ?? 25}
                onChange={(e) => setEmailConfig({ ...emailConfig, smtpPort: Number(e.target.value) })}
                disabled={isLoading}
              />
            </Form.Group>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-6">
            <Form.Group>
              <Form.Label>From Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="e.g. noreply@pandora.net"
                value={emailConfig.fromEmail || ''}
                onChange={(e) => setEmailConfig({ ...emailConfig, fromEmail: e.target.value })}
                disabled={isLoading}
              />
            </Form.Group>
          </div>
          <div className="col-6">
            <Form.Group>
              <Form.Label>From Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. HR Consent VN System"
                value={emailConfig.fromName || ''}
                onChange={(e) => setEmailConfig({ ...emailConfig, fromName: e.target.value })}
                disabled={isLoading}
              />
            </Form.Group>
          </div>
        </div>
        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="enable-ssl-switch"
            label="Enable SSL"
            checked={emailConfig.enableSsl ?? false}
            onChange={(e) => setEmailConfig({ ...emailConfig, enableSsl: e.target.checked })}
            disabled={isLoading}
          />
        </Form.Group>

        {/* Last Updated Info */}
        {emailConfig.updatedDate && (
          <div className="mt-3 text-muted small">
            <Icon.InfoCircle className="me-1" />
            Last updated: {new Date(emailConfig.updatedDate).toLocaleString()}
            {emailConfig.updatedBy && ` by ${emailConfig.updatedBy}`}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
